/**
 * Warehouse layer module.
 *
 * Renders warehouses as styled circle markers with type/status coloring.
 */

import type { Map } from 'mapbox-gl';
import type { MapWarehouse } from '../types/mapTypes';
import { warehouseToFeature, featureCollection } from '../utils/geojson';

const SOURCE_ID = 'warehouses-source';
const CIRCLE_LAYER_ID = 'warehouse-circles';
const LABEL_LAYER_ID = 'warehouse-labels';

function warehouseColor(status: string): string {
  switch (status) {
    case 'operational': return '#10B981';
    case 'maintenance_due': return '#F59E0B';
    case 'offline': return '#EF4444';
    default: return '#6B7280';
  }
}

export function addWarehouseLayers(map: Map): void {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: featureCollection([]),
  });

  map.addLayer({
    id: CIRCLE_LAYER_ID,
    type: 'circle',
    source: SOURCE_ID,
    layout: {},
    paint: {
      'circle-color': ['get', '_color'],
      'circle-radius': 10,
      'circle-stroke-width': 3,
      'circle-stroke-color': '#FFFFFF',
    },
    filter: ['==', ['get', '_type'], 'warehouse'],
  });

  map.addLayer({
    id: LABEL_LAYER_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': 11,
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
      'text-offset': [0, 1.5],
    },
    paint: {
      'text-color': '#374151',
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 1,
    },
    filter: ['==', ['get', '_type'], 'warehouse'],
  });
}

export function updateWarehouseData(map: Map, warehouses: MapWarehouse[]): void {
  const source = map.getSource(SOURCE_ID);
  if (!source || source.type !== 'geojson') return;

  const features = warehouses.map((wh) => {
    const feature = warehouseToFeature(wh);
    (feature.properties as any)._color = warehouseColor(wh.status);
    return feature;
  });

  (source as any).setData(featureCollection(features));
}

export function removeWarehouseLayers(map: Map): void {
  if (map.getLayer(LABEL_LAYER_ID)) map.removeLayer(LABEL_LAYER_ID);
  if (map.getLayer(CIRCLE_LAYER_ID)) map.removeLayer(CIRCLE_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

export { SOURCE_ID as warehouseSourceId };
